import { defineQuery } from 'next-sanity'
import type {
  ALL_CONTENT_BLOCKS_QUERY_RESULT,
  LOCATION_BY_SLUG_QUERY_RESULT,
} from '@/sanity/types'
import { inlineCtaFields, contactFormFields } from './fragments/atoms'
import { contentBlockFragment } from './fragments/blocks'

/**
 * ContactForm fields as returned by the query (without document metadata)
 */
export type ContactFormFields = {
  _type: 'contactForm'
  title: string
  ctaTitle: string
  formTitle: string
  formSubTitle: string
  pipelineId: string
  successHeading?: string | null | undefined
  successMessage?: string | null | undefined
}

/**
 * Type helper for location queries
 *
 * LocationWithDefaults matches the actual query result structure from LOCATION_BY_SLUG_QUERY.
 * The component handles null checks for content, teamMembers, and processingInformation.
 */
export type LocationWithDefaults = NonNullable<LOCATION_BY_SLUG_QUERY_RESULT> & {
  // Override content to use the correct type (can be null or array)
  content: ALL_CONTENT_BLOCKS_QUERY_RESULT | null
} & {
  // Add contactForm field (these will be in the generated types after regeneration)
  contactForm?: ContactFormFields | null
}

/**
 * Get all location slugs for static generation
 */
export const ALL_LOCATION_SLUGS_QUERY = defineQuery(
  `*[_type == "location"]{ "slug": slug.current }`
)

/**
 * Get all location slugs with updatedAt for revalidation
 */
export const ALL_LOCATION_SLUGS_WITH_UPDATED_AT_QUERY = defineQuery(
  `*[_type == "location"]{ "slug": slug.current, _updatedAt }`
)

/**
 * Get all locations for search/filtering - lightweight projection
 */
export const ALL_LOCATIONS_FOR_SEARCH_QUERY =
  defineQuery(`*[_type == "location"]{
    _id,
    _type,
    locationName,
    "slug": slug.current,
    mainImage,
    coordinates{
      lat,
      lng
    },
    address,
    "region": region[]->{
      _id,
      _type,
      name
    },
    "amenities": amenities[]->{
      _id,
      _type,
      name
    },
    badges{
      ofstedGood,
      ofstedOutstanding,
      eco
    },
    tourUrl,
    tourBooking{
      tourBookingUrl
    },
    "admissionsPartner": admissionsPartner->{
      name,
      callBookingUrl
    }
  }`)

/**
 * Get all locations that have at least one booking URL (tour or call)
 * Used by the Calendly Flow popup in the masthead
 */
export const ALL_LOCATIONS_WITH_BOOKING_QUERY =
  defineQuery(`*[_type == "location" && (
    defined(tourBooking.tourBookingUrl) ||
    defined(admissionsPartner)
  )]{
    _id,
    locationName,
    "slug": slug.current,
    tourBooking{
      tourBookingUrl
    },
    "admissionsPartner": admissionsPartner->{
      name,
      callBookingUrl
    }
  } | order(locationName asc)`)

/**
 * Get location by slug - full document including content blocks
 */
export const LOCATION_BY_SLUG_QUERY =
  defineQuery(`*[_type == "location" && slug.current == $slug][0]{
    _id,
    _type,
    locationName,
    "slug": slug.current,
    mainImage,
    "videoHero": videoHero->{
      title,
      "video": video.asset->,
      startTime,
      endTime
    },
    badges{
      ofstedGood,
      ofstedOutstanding,
      eco
    },
    region[],
    coordinates,
    tourUrl,
    tourBooking{
      tourBookingUrl
    },
    "admissionsPartner": admissionsPartner->{
      name,
      callBookingUrl
    },
    "contactForm": contactForm->{
      ${contactFormFields}
    },
    address,
    openingHours,
    extendedOpeningHours,
    "prices": fees{
      "title": rates.title,
      "description": rates.description,
      "disclaimer": rates.disclaimer,
      "cta": rates.cta{
        ${inlineCtaFields}
      },
      "explanation": rates.explanation,
      "explanationImage": rates.explanation.image,
      "explanationText": rates.explanation.text[],
      "consumableDescriptions": rates.consumableDescriptions,
      "monthlyFees": rates.monthlyFees
    },
    seo,
    "content": content.blockContent[]->{
      ${contentBlockFragment}
    },
    "teamMembers": teamMembers[]->{
      _type,
      role,
      name,
      description,
      image
    },
    processingInformation[]{
      _key,
      title,
      image,
      content
    },
    "puzzlePieces": puzzlePieces->{
      _type,
      title,
      pieces[]{
        title,
        subTitle,
        image{
          _type,
          asset,
          crop,
          hotspot,
          altText
        },
        "CTA": cta {
          ${inlineCtaFields}
        },
        backgroundColor
      }
    }
  }`)

/**
 * Get location by slug - streaming version (metadata only, no content blocks)
 */
export const LOCATION_BY_SLUG_STREAMING_QUERY =
  defineQuery(`*[_type == "location" && slug.current == $slug][0]{
    _id,
    _type,
    locationName,
    "slug": slug.current,
    mainImage,
    seo
  }`)

/**
 * Get location content blocks separately (for streaming)
 */
export const LOCATION_CONTENT_BLOCKS_QUERY =
  defineQuery(`*[_type == "location" && _id == $locationId][0]{
    "content": content.blockContent[]->{
      ${contentBlockFragment}
    }
  }`)

/**
 * Query to fetch nursery-search-block document (for filter options)
 * Fetches the first nursery-search-block document found
 * Note: This duplicates the fragment query - consider using the fragment instead
 */
export const NURSERY_SEARCH_BLOCK_FOR_LOCATION_QUERY =
  defineQuery(`*[_type == "nursery-search-block"][0]{
    _type,
    title,
    includeSearch,
    includeMap,
    "regionsFilters": regionsFilters[]->{
      _id,
      _type,
      name
    },
    "regionsPreSelected": regionsPreSelected[]->{
      _id,
      _type,
      name
    },
    "amenities": amenities[]->{
      _id,
      _type,
      name
    }
  }`)
